import React, { useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container, Button, ProgressBar, Form, Alert } from "react-bootstrap";
import * as THREE from "three";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB chunks
const SERVER_UPLOAD_URL = `${process.env.REACT_APP_API_URL}/v1/1/venue/1/image_upload`;

const App: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const uploadChunks = async (file: File) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const identifier = `${Date.now()}-${file.name}`;

    setProgress(0);
    setMessage(null);

    for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
      const start = (chunkNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("chunk_number", chunkNumber.toString());
      formData.append("total_chunks", totalChunks.toString());
      formData.append("identifier", identifier);
      formData.append("filename", file.name);
      formData.append("file_name", file.name);

      try {
        await fetch(SERVER_UPLOAD_URL, {
          method: "POST",
          body: formData,
        });
        setProgress((chunkNumber / totalChunks) * 100);
      } catch (error) {
        console.error("Error uploading chunk:", error);
        setMessage("Error uploading file. Please try again.");
        return;
      }
    }

    setMessage("File uploaded successfully!");
    loadEXR(identifier);
  };

  // Call loadEXR on document load
  React.useEffect(() => {
    loadEXR("test.exr");
  }, []);

  const loadEXR = (identifier: string) => {
    // if (!file) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 2, 0.1, 1000);
    camera.position.set(0, 0, 2);

    const renderer = new THREE.WebGLRenderer();

    if (canvasRef.current) {
      canvasRef.current.innerHTML = ""; // Clear previous render
      renderer.setSize(canvasRef.current.offsetWidth, canvasRef.current.offsetHeight);
      canvasRef.current.appendChild(renderer.domElement);
    }

    // Add lighting for better rendering
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(1, 1, 1).normalize();
    scene.add(directionalLight);

    // Add OrbitControls for navigation
    const controls = new OrbitControls(camera, renderer.domElement);

    const exrLoader = new EXRLoader();
    exrLoader.load(
      `https://teleport.estatelabs.ca/uploads/1/test.jpg`, // Replace with your server's final file URL
      (texture) => {
        const material = new THREE.MeshBasicMaterial({ map: texture });
        const geometry = new THREE.PlaneGeometry(1, 1);
        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        const animate = () => {
          requestAnimationFrame(animate);
          controls.update(); // Enable OrbitControls
          renderer.render(scene, camera);
        };
        animate();
      },
      undefined,
      (error) => {
        console.error("Error loading EXR file:", error);
        setMessage("Error loading EXR file. Please try again.");
      }
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile?.name.endsWith(".exr")) {
      setFile(selectedFile);
      setMessage(null);
    } else {
      setMessage("Please select a valid EXR file.");
    }
  };

  const handleUpload = () => {
    if (file) {
      uploadChunks(file);
    } else {
      setMessage("Please select a file to upload.");
    }
  };

  return (
    <Container className="mt-5">
      <h1 className="text-center">EXR File Uploader & Viewer</h1>
      <Form.Group controlId="formFile" className="my-3">
        <Form.Label>Select an EXR file</Form.Label>
        <Form.Control type="file" accept=".jpg" onChange={handleFileChange} />
      </Form.Group>
      <Button onClick={handleUpload} disabled={!file}>
        Upload
      </Button>
      {progress > 0 && <ProgressBar now={progress} label={`${Math.round(progress)}%`} className="my-3" />}
      {message && <Alert variant="info">{message}</Alert>}
      <div ref={canvasRef} className="mt-4" style={{ width: "100%", height: "500px", border: "1px solid #ddd" }} />
    </Container>
  );
};

export default App;
